import { useCallback } from 'react'
import { useQuery, useQueryClient, type Query } from '@tanstack/react-query'
import { useAuth } from './use-auth'
import { authorizedFetch } from '@/lib/api'
import { API_BASE_URL } from '@/constants'
import { useStreamingSse, type UseStreamingSseResult } from './use-streaming-sse'

export interface CallRecordListItem {
  id: string
  vapi_call_id: string
  call_status: string
  is_complete: boolean
  call_type: string
  assistant_name: string
  customer_number: string
  customer_name: string
  started_at: string | null
  ended_at: string | null
  duration_seconds?: number | null
  ended_reason: string
  evaluation_status: string
  evaluated_at: string | null
  created_at: string
  updated_at: string
  /** Live board SSE only; null clears a previous preview */
  live_transcript_preview?: string | null
  live_transcript_role?: string | null
  /** Live board SSE: semantic kind of `live_transcript_preview` (REST list is null). */
  live_preview_kind?: 'transcript' | 'speech_status' | 'conversation' | null
  /** Truncated post-call summary for list/board (REST always sends; SSE may omit) */
  summary_preview?: string
  /** Present on REST list; SSE partials may omit */
  evaluation_summary?: string
  triage_summary?: string
  call_state?: string
  triage_updated_at?: string | null
  triage_generation?: number
  /** Extra row keys added by board extensions (tenant packs). */
  [extension: string]: unknown
}

export interface CallRecordDetail extends CallRecordListItem {
  transcript: string
  summary: string
  recording_url: string
  /** Platform path for playback. Empty when the call has no recording. */
  recording_playback_url?: string
  evaluation_summary: string
  provider_context: Record<string, unknown>
  message_count: number
  last_message_text: string
  last_message_role: string
  last_event_type: string
  last_event_at: string | null
  live_transcript_preview?: string | null
  live_transcript_role?: string | null
}

export interface CallMessageItem {
  id: string
  sequence: number
  role: string
  text: string
  turn_id: string
  source_type: string
  is_final: boolean
  event_ts: string | null
  received_at: string
  updated_at: string
  metadata_json: Record<string, unknown>
}

export interface CallEventItem {
  id: string
  message_type: string
  received_at: string
  event_ts: string | null
  turn_id: string
  payload: Record<string, unknown>
}

interface LiveCallEnvelope {
  v: number
  domain: string
  type: string
  payload: {
    record?: Partial<CallRecordDetail> & { id: string }
    delta?: {
      role?: string
      transcript?: string
      transcript_type?: string
      conversation_messages?: number
      speech_activity?: boolean
      status?: string
    }
  }
}

/** Only `['call-records', limit]` — not detail keys `['call-records', uuid]`. */
function isCallRecordListQuery(query: Query): boolean {
  const key = query.queryKey
  return (
    Array.isArray(key) && key[0] === 'call-records' && typeof key[1] === 'number'
  )
}

export type UseLiveCallRecordsStreamOptions = {
  enabled?: boolean
  /** Query key root to invalidate after terminal call events (e.g. a pack's outcomes list). */
  invalidateOnTerminal?: string
}

function resolveLiveStreamOptions(
  enabledOrOptions?: boolean | UseLiveCallRecordsStreamOptions
): { enabled: boolean; invalidateOnTerminal: string | null } {
  if (typeof enabledOrOptions === 'boolean' || enabledOrOptions === undefined) {
    return { enabled: enabledOrOptions !== false, invalidateOnTerminal: null }
  }
  return {
    enabled: enabledOrOptions.enabled !== false,
    invalidateOnTerminal: enabledOrOptions.invalidateOnTerminal ?? null,
  }
}

const LIST_ROW_CORE_KEYS = new Set([
  'id',
  'vapi_call_id',
  'call_status',
  'is_complete',
  'call_type',
  'assistant_name',
  'customer_number',
  'customer_name',
  'started_at',
  'ended_at',
  'duration_seconds',
  'ended_reason',
  'evaluation_status',
  'evaluated_at',
  'created_at',
  'updated_at',
  'live_transcript_preview',
  'live_transcript_role',
  'live_preview_kind',
  'summary_preview',
  'evaluation_summary',
  'triage_summary',
  'call_state',
  'triage_updated_at',
  'triage_generation',
])

function extensionKeysEqual(a: CallRecordListItem, b: CallRecordListItem): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)])
  for (const key of keys) {
    if (LIST_ROW_CORE_KEYS.has(key)) continue
    if (JSON.stringify(a[key] ?? null) !== JSON.stringify(b[key] ?? null)) return false
  }
  return true
}

function listRowContentEqual(a: CallRecordListItem, b: CallRecordListItem): boolean {
  if (!extensionKeysEqual(a, b)) return false
  return (
    a.id === b.id &&
    a.vapi_call_id === b.vapi_call_id &&
    a.call_status === b.call_status &&
    a.is_complete === b.is_complete &&
    a.call_type === b.call_type &&
    a.assistant_name === b.assistant_name &&
    a.customer_number === b.customer_number &&
    a.customer_name === b.customer_name &&
    a.started_at === b.started_at &&
    a.ended_at === b.ended_at &&
    (a.duration_seconds ?? null) === (b.duration_seconds ?? null) &&
    a.ended_reason === b.ended_reason &&
    a.evaluation_status === b.evaluation_status &&
    a.evaluated_at === b.evaluated_at &&
    a.created_at === b.created_at &&
    a.updated_at === b.updated_at &&
    (a.live_transcript_preview ?? null) === (b.live_transcript_preview ?? null) &&
    (a.live_transcript_role ?? null) === (b.live_transcript_role ?? null) &&
    (a.live_preview_kind ?? null) === (b.live_preview_kind ?? null) &&
    (a.summary_preview ?? '') === (b.summary_preview ?? '') &&
    (a.evaluation_summary ?? '') === (b.evaluation_summary ?? '') &&
    (a.triage_summary ?? '') === (b.triage_summary ?? '') &&
    (a.call_state ?? 'routine') === (b.call_state ?? 'routine') &&
    (a.triage_updated_at ?? null) === (b.triage_updated_at ?? null) &&
    (a.triage_generation ?? 0) === (b.triage_generation ?? 0)
  )
}

function upsertCallRecord<T extends CallRecordListItem>(
  rows: T[] | undefined,
  incoming: Partial<T> & { id: string }
): T[] {
  const existing = rows ?? []
  const prevById = new Map(existing.map((row) => [row.id, row]))
  const mergedForIncoming = prevById.has(incoming.id)
    ? ({ ...prevById.get(incoming.id)!, ...incoming } as T)
    : (incoming as T)

  const next: T[] = prevById.has(incoming.id)
    ? existing.map((row) => (row.id === incoming.id ? mergedForIncoming : row))
    : ([mergedForIncoming, ...existing] as T[])

  const sorted = [...next].sort((a, b) => b.created_at.localeCompare(a.created_at))

  const stabilized: T[] = sorted.map((row) => {
    const prev = prevById.get(row.id)
    if (prev && listRowContentEqual(prev, row)) return prev as T
    return row
  })

  if (
    stabilized.length === existing.length &&
    stabilized.every((row, i) => row === existing[i])
  ) {
    return existing
  }

  return stabilized
}

function mergeCallDetail(
  current: CallRecordDetail | undefined,
  incoming: Partial<CallRecordDetail> & { id: string },
  eventType?: string,
  delta?: LiveCallEnvelope['payload']['delta']
): CallRecordDetail | undefined {
  if (!current) {
    return incoming as CallRecordDetail
  }

  const merged: CallRecordDetail = { ...current, ...incoming }

  if (eventType === 'call.transcript_delta') {
    if (delta?.speech_activity) {
      const role = (delta.role || 'call').trim() || 'call'
      const status = (delta.status || '…').trim() || '…'
      merged.live_transcript_preview = status
      merged.live_transcript_role = role
      merged.live_preview_kind = 'speech_status'
    } else if (delta?.transcript) {
      if (delta.conversation_messages != null) {
        merged.transcript = delta.transcript
        merged.live_transcript_preview = null
        merged.live_transcript_role = null
        merged.live_preview_kind = null
      } else if (delta.transcript_type === 'final') {
        const line = `${delta.role || 'unknown'}: ${delta.transcript}`.trim()
        const existingLines = merged.transcript ? merged.transcript.split('\n') : []
        if (!existingLines.includes(line)) {
          merged.transcript = merged.transcript ? `${merged.transcript}\n${line}` : line
        }
        merged.live_transcript_preview = null
        merged.live_transcript_role = null
        merged.live_preview_kind = null
      } else {
        merged.live_transcript_preview = delta.transcript
        merged.live_transcript_role = delta.role || null
        merged.live_preview_kind = 'transcript'
      }
    }
  }

  if (eventType === 'call.completed') {
    merged.live_transcript_preview = null
    merged.live_transcript_role = null
    merged.live_preview_kind = null
  }

  return merged
}

export function useCallRecords(limit = 50) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-records', limit],
    queryFn: async (): Promise<CallRecordListItem[]> => {
      if (!token) throw new Error('Not authenticated')
      const params = new URLSearchParams({ limit: String(limit) })
      const res = await authorizedFetch(`/api/call-records/?${params}`)
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch call records')
      return res.json()
    },
    enabled: !!token,
  })
}

export function useCallRecord(id: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-records', id],
    queryFn: async (): Promise<CallRecordDetail> => {
      if (!token || !id) throw new Error('Not authenticated')
      const res = await authorizedFetch(`/api/call-records/${id}`)
      if (res.status === 404) throw new Error('Not found')
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch call record')
      return res.json()
    },
    enabled: !!token && !!id,
  })
}

export function useCallMessages(id: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-record-messages', id],
    queryFn: async (): Promise<CallMessageItem[]> => {
      if (!token || !id) throw new Error('Not authenticated')
      const res = await authorizedFetch(`/api/call-records/${id}/messages`)
      if (res.status === 404) throw new Error('Not found')
      if (res.status === 403) throw new Error('Forbidden')
      if (!res.ok) throw new Error('Failed to fetch call messages')
      return res.json()
    },
    enabled: !!token && !!id,
  })
}

/** Matches `EVENT_PAGE_SIZE` in `api/apps/calls/api.py`. A full page means another follows. */
const CALL_EVENT_PAGE_SIZE = 500

export function useCallEvents(id: string | undefined) {
  const { token } = useAuth()

  return useQuery({
    queryKey: ['call-record-events', id],
    queryFn: async (): Promise<CallEventItem[]> => {
      if (!token || !id) throw new Error('Not authenticated')
      const all: CallEventItem[] = []
      let cursor: string | undefined
      for (;;) {
        const params = new URLSearchParams()
        if (cursor) params.set('cursor', cursor)
        const query = params.toString()
        const res = await authorizedFetch(
          `/api/call-records/${id}/events${query ? `?${query}` : ''}`
        )
        if (res.status === 404) throw new Error('Not found')
        if (res.status === 403) throw new Error('Forbidden')
        if (!res.ok) throw new Error('Failed to fetch call events')
        const page = (await res.json()) as CallEventItem[]
        all.push(...page)
        if (page.length < CALL_EVENT_PAGE_SIZE) return all
        const last = page[page.length - 1]
        cursor = `${last.received_at}|${last.id}`
      }
    },
    enabled: !!token && !!id,
  })
}

export function useLiveCallRecordsStream(
  enabledOrOptions?: boolean | UseLiveCallRecordsStreamOptions
): UseStreamingSseResult {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const { enabled, invalidateOnTerminal } = resolveLiveStreamOptions(enabledOrOptions)

  const onMessage = useCallback(
    (message: { data: string }) => {
      const parsed: LiveCallEnvelope = JSON.parse(message.data)
      if (parsed.domain !== 'calls' || !parsed.payload?.record) return

      queryClient.setQueriesData<CallRecordListItem[]>(
        { predicate: isCallRecordListQuery },
        (current) => upsertCallRecord(current, parsed.payload.record as CallRecordListItem)
      )

      if (invalidateOnTerminal) {
        const rec = parsed.payload.record
        const terminal =
          parsed.type === 'call.completed' ||
          rec.is_complete === true ||
          rec.call_status === 'ended'
        if (terminal) {
          void queryClient.invalidateQueries({ queryKey: [invalidateOnTerminal] })
        }
      }
    },
    [queryClient, invalidateOnTerminal]
  )

  const url = API_BASE_URL ? `${API_BASE_URL}/api/call-records/live` : '/api/call-records/live'
  return useStreamingSse({ url, token, enabled, onMessage })
}

export function useLiveCallRecordStream(
  id: string | undefined,
  enabled = true
): UseStreamingSseResult {
  const { token } = useAuth()
  const queryClient = useQueryClient()

  const onMessage = useCallback(
    (message: { data: string }) => {
      if (!id) return
      const parsed: LiveCallEnvelope = JSON.parse(message.data)
      if (parsed.domain !== 'calls' || !parsed.payload?.record) return

      const record = parsed.payload.record as Partial<CallRecordDetail> & { id: string }

      queryClient.setQueryData<CallRecordDetail | undefined>(
        ['call-records', id],
        (current) => mergeCallDetail(current, record, parsed.type, parsed.payload.delta)
      )

      queryClient.setQueriesData<CallRecordListItem[]>(
        { predicate: isCallRecordListQuery },
        (current) => upsertCallRecord(current, record as CallRecordListItem)
      )

      if (
        parsed.type === 'call.transcript_delta' ||
        parsed.type === 'call.completed' ||
        parsed.type === 'call.status_changed'
      ) {
        void queryClient.invalidateQueries({ queryKey: ['call-record-messages', id] })
        void queryClient.invalidateQueries({ queryKey: ['call-record-events', id] })
      }
    },
    [id, queryClient]
  )

  const url = id
    ? API_BASE_URL
      ? `${API_BASE_URL}/api/call-records/${id}/live`
      : `/api/call-records/${id}/live`
    : ''
  return useStreamingSse({ url, token, enabled: enabled && !!id, onMessage })
}
